import React from 'react';
import RichTextEditor from './RichTextEditor';

function CurriculumEditor({ pageContent, setPageContent, uploading, uploadingField, handleFileUpload, handleImageUpload, deleteImageFromFirebase, deleteFileFromStorage }) {
  
  const handleCurriculumFileUpload = async (file) => {
    if (!file) return;
    
    try {
      const { uploadFile } = await import('../../firebase/firebase-config');
      const fileData = await uploadFile(file, `curriculum/${Date.now()}_${file.name}`);
      setPageContent({...pageContent, curriculumFile: fileData});
      alert('✅ Curriculum file uploaded successfully!');
    } catch (error) {
      alert('❌ Error uploading file: ' + error.message);
    }
  };

  const handleDeleteCurriculumFile = async () => {
    if (!pageContent.curriculumFile?.url) return;
    
    if (window.confirm('Delete this curriculum file permanently?')) {
      try {
        const { deleteFileFromStorage } = await import('../../firebase/firebase-config');
        await deleteFileFromStorage(pageContent.curriculumFile.url);
        setPageContent({...pageContent, curriculumFile: null});
        alert('✅ File deleted successfully!');
      } catch (error) {
        alert('❌ Error deleting file: ' + error.message);
      }
    }
  };

  return (
    <div className="page-editor">
      <div className="editor-card">
        <h3>📖 Curriculum Page Editor</h3>
        
        <div className="form-group">
          <label>Page Title</label>
          <input 
            value={pageContent.title || 'Curriculum'} 
            onChange={(e) => setPageContent({...pageContent, title: e.target.value})} 
            className="form-input" 
            placeholder="e.g., Curriculum"
          />
        </div>
        
        {/* Featured Image Section */}
        <div className="form-group">
          <label>Featured Image</label>
          {pageContent.featuredImage && (
            <div className="image-preview-container">
              <img src={pageContent.featuredImage} className="preview-img" />
            </div>
          )}
          <div className="button-group">
            <label className="upload-label">
              {uploading && uploadingField === 'featuredImage' ? '⏳ Uploading...' : '📷 Upload Featured Image'}
              <input 
                type="file" 
                accept="image/*" 
                style={{display:'none'}} 
                onChange={async (e) => {
                  if (e.target.files[0]) {
                    const { uploadImage } = await import('../../firebase/firebase-config');
                    const url = await uploadImage(e.target.files[0], `curriculum/featured_${Date.now()}`);
                    setPageContent({...pageContent, featuredImage: url});
                    alert('✅ Featured image uploaded!');
                  }
                }} 
              />
            </label>
            {pageContent.featuredImage && (
              <button className="remove-btn" onClick={async () => {
                if (window.confirm('Delete this image permanently?')) {
                  const { deleteImageFromStorage } = await import('../../firebase/firebase-config');
                  await deleteImageFromStorage(pageContent.featuredImage);
                  setPageContent({...pageContent, featuredImage: ''});
                  alert('✅ Image deleted!');
                }
              }}>🗑️ Delete Image</button>
            )}
          </div>
          <small className="help-text">
            This image will appear at the top of the curriculum page (Recommended size: 1200×400px)
          </small>
        </div>
        
        {/* Overview Section with Rich Text */}
        <div className="form-group">
          <label>Curriculum Overview (Rich Text)</label>
          <RichTextEditor 
            value={pageContent.overview || ''}
            onChange={(html) => setPageContent({...pageContent, overview: html})}
            placeholder="Write an overview of the curriculum..."
            imageFolder="curriculum-overview-images"
          />
          <small className="help-text">
            Describe the curriculum structure, board affiliation, and educational approach
          </small>
        </div>
        
        {/* Subjects Section with Rich Text */}
        <div className="form-group">
          <label>Subjects Offered (Rich Text)</label>
          <RichTextEditor 
            value={pageContent.subjects || ''}
            onChange={(html) => setPageContent({...pageContent, subjects: html})}
            placeholder="List all subjects offered..."
            imageFolder="curriculum-subjects-images"
          />
          <small className="help-text">
            Use bullet points or tables to list subjects by grade level
          </small>
        </div>
        
        {/* Teaching Methodology with Rich Text */}
        <div className="form-group">
          <label>Teaching Methodology (Rich Text)</label>
          <RichTextEditor 
            value={pageContent.methodology || ''}
            onChange={(html) => setPageContent({...pageContent, methodology: html})}
            placeholder="Describe the teaching methodology..."
            imageFolder="curriculum-methodology-images"
          />
          <small className="help-text">
            Explain teaching approaches, classroom strategies, and learning techniques
          </small>
        </div>
        
        {/* Assessment Pattern with Rich Text */}
        <div className="form-group">
          <label>Assessment Pattern (Rich Text)</label>
          <RichTextEditor 
            value={pageContent.assessment || ''}
            onChange={(html) => setPageContent({...pageContent, assessment: html})}
            placeholder="Describe the assessment pattern..."
            imageFolder="curriculum-assessment-images"
          />
          <small className="help-text">
            Explain evaluation methods, exam patterns, and grading system
          </small>
        </div>
        
        {/* Curriculum File Upload */}
        <div className="form-group">
          <label>Curriculum File (PDF/Image/Document)</label>
          {pageContent.curriculumFile && pageContent.curriculumFile.url && (
            <div className="file-info">
              <div className="file-preview">
                <span className="file-icon">📄</span>
                <a href={pageContent.curriculumFile.url} target="_blank" rel="noopener noreferrer" className="file-link">
                  {pageContent.curriculumFile.name || 'Curriculum File'}
                </a>
              </div>
              <button className="remove-btn-small" onClick={handleDeleteCurriculumFile}>🗑️ Delete File</button>
            </div>
          )}
          <div className="button-group">
            <label className="upload-label">
              {uploading && uploadingField === 'curriculumFile' ? '⏳ Uploading...' : '📎 Upload Curriculum File'}
              <input 
                type="file" 
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" 
                style={{display:'none'}} 
                onChange={async (e) => {
                  if (e.target.files[0]) {
                    await handleCurriculumFileUpload(e.target.files[0]);
                    e.target.value = '';
                  }
                }} 
              />
            </label>
          </div>
          <small className="help-text">
            Supported formats: PDF, Images, Word documents (Max size: 10MB)
          </small>
        </div>
      </div>

      <style>{`
        .page-editor {
          max-width: 1200px;
          margin: 0 auto;
        }

        .editor-card {
          background: #fff;
          border-radius: 16px;
          padding: 28px;
          margin-bottom: 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .editor-card h3 {
          margin: 0 0 24px;
          color: #1a3a6b;
          font-size: 24px;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 12px;
        }

        .form-group {
          margin-bottom: 28px;
        }

        .form-group label {
          display: block;
          margin-bottom: 10px;
          font-weight: 700;
          color: #1e293b;
          font-size: 15px;
        }

        .form-input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.3s;
        }

        .form-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }

        .button-group {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }

        .add-btn {
          background: #10b981;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          transition: background 0.3s;
        }

        .add-btn:hover {
          background: #059669;
        }

        .remove-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: background 0.3s;
        }

        .remove-btn:hover {
          background: #dc2626;
        }

        .remove-btn-small {
          background: #ef4444;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          transition: background 0.3s;
        }

        .remove-btn-small:hover {
          background: #dc2626;
        }

        .upload-label {
          background: #3b82f6;
          color: white;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          display: inline-block;
          transition: background 0.3s;
        }

        .upload-label:hover {
          background: #2563eb;
        }

        .preview-img {
          width: 200px;
          height: 120px;
          object-fit: contain;
          border-radius: 10px;
          border: 2px solid #e2e8f0;
          background: #f5f5f5;
        }

        .image-preview-container {
          margin-bottom: 12px;
        }

        .file-info {
          background: #f1f5f9;
          padding: 12px 16px;
          border-radius: 10px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .file-preview {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .file-icon {
          font-size: 28px;
        }

        .file-link {
          color: #1a3a6b;
          text-decoration: none;
          font-weight: 500;
        }

        .file-link:hover {
          text-decoration: underline;
        }

        .help-text {
          display: block;
          margin-top: 8px;
          color: #64748b;
          font-size: 12px;
        }

        @media (max-width: 768px) {
          .editor-card {
            padding: 20px;
          }
          
          .editor-card h3 {
            font-size: 20px;
          }
          
          .button-group {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .preview-img {
            width: 150px;
            height: 90px;
          }
          
          .file-info {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  );
}

export default CurriculumEditor;